import { useState, useEffect, useRef } from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
// TRADITIONAL_GRADING_PATCH_20260813
import CourseBasicSettings from './course/CourseBasicSettings';
import CourseAssignments from './course/CourseAssignments';
import CourseAttendance from './course/CourseAttendance';
import CourseSyllabus from './course/CourseSyllabus';
import CourseAiPermissions from './course/CourseAiPermissions';
import CourseStudentMeetings from './course/CourseStudentMeetings';
import CourseSelfEvaluation from './course/CourseSelfEvaluation';
import CourseStudents from './course/CourseStudents';
import CourseSchedule from './course/CourseSchedule';
import CourseDocuments from './course/CourseDocuments';
import CourseNotifications from './course/CourseNotifications';
import InstructorResponsibilities
  from './course/InstructorResponsibilities';
import CourseExtendModal
  from './course/CourseExtendModal';
import CourseImportModal
  from './course/CourseImportModal';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const ALL_TABS = [
  { key: 'basic', label: 'Basic Settings' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'attendance', label: 'Attendance', requiresToggle: 'attendance_enabled' },
  { key: 'syllabus', label: 'Syllabus' },
  { key: 'schedule', label: 'Teaching Schedule' },
  { key: 'ai', label: 'AI Permissions' },
  {
    key: 'meetings',
    label: 'Student Meetings',
    requiresToggle: 'self_eval_enabled'
  },
    { key: 'selfeval', label: 'Self-Evaluation', requiresToggle: 'self_eval_enabled' },
  { key: 'students', label: 'Students' },
  {
  key: 'responsibilities',
  label: 'Instructor Responsibilities',
  requiresMultipleInstructors: true
},
  { key: 'documents', label: 'Documents' },
  { key: 'notifications', label: 'Notifications' },
];

const SECONDARY_EDITABLE_TABS =
  new Set([
    'schedule',
    'meetings',
    'students',
    'documents',
    'notifications'
  ]);


function ReadOnlyCourseSection({
  enabled,
  children
}) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (
      !enabled ||
      !rootRef.current
    ) {
      return undefined;
    }

    const root =
      rootRef.current;

    const mutationButtonPattern =
      /\b(save|add|create|edit|delete|remove|update|apply|rename|upload|assign|unassign|clear|reset|enable|disable|set|change|replace|configure|manage|move|reorder|duplicate|publish|unpublish|generate|import|sync)\b|\bcopy\s+(from|into|group|membership|setup)\b/i;

    function makeReadOnly() {
      root
        .querySelectorAll(
          'textarea'
        )
        .forEach(control => {
          if (!control.readOnly) {
            control.readOnly = true;
          }

          control.setAttribute(
            'aria-readonly',
            'true'
          );

          control.setAttribute(
            'data-secondary-readonly-field',
            'true'
          );
        });

      root
        .querySelectorAll('input')
        .forEach(control => {
          const type =
            String(
              control.type || 'text'
            ).toLowerCase();

          const needsDisable =
            [
              'checkbox',
              'radio',
              'file',
              'range',
              'color',
              'button',
              'submit',
              'reset'
            ].includes(type);

          if (needsDisable) {
            if (!control.disabled) {
              control.disabled = true;
            }

            control.setAttribute(
              'aria-disabled',
              'true'
            );
          } else {
            if (!control.readOnly) {
              control.readOnly = true;
            }

            control.setAttribute(
              'aria-readonly',
              'true'
            );
          }

          control.setAttribute(
            'data-secondary-readonly-field',
            'true'
          );
        });

      root
        .querySelectorAll('select')
        .forEach(control => {
          if (!control.disabled) {
            control.disabled = true;
          }

          control.setAttribute(
            'aria-disabled',
            'true'
          );

          control.setAttribute(
            'data-secondary-readonly-field',
            'true'
          );
        });

      root
        .querySelectorAll(
          '[contenteditable="true"]'
        )
        .forEach(control => {
          control.setAttribute(
            'contenteditable',
            'false'
          );

          control.setAttribute(
            'aria-readonly',
            'true'
          );

          control.setAttribute(
            'data-secondary-readonly-field',
            'true'
          );
        });

      root
        .querySelectorAll('button')
        .forEach(button => {
          const label = [
            button.textContent,
            button.getAttribute(
              'aria-label'
            ),
            button.getAttribute(
              'title'
            )
          ]
            .filter(Boolean)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

          const isMutationButton =
            button.type === 'submit' ||
            mutationButtonPattern.test(
              label
            );

          if (!isMutationButton) {
            return;
          }

          if (!button.disabled) {
            button.disabled = true;
          }

          button.setAttribute(
            'aria-disabled',
            'true'
          );

          button.setAttribute(
            'data-secondary-readonly-action',
            'true'
          );
        });
    }

    makeReadOnly();

    const observer =
      new MutationObserver(
        makeReadOnly
      );

    observer.observe(
      root,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'disabled',
          'readonly',
          'contenteditable'
        ]
      }
    );

    return () => {
      observer.disconnect();
    };
  }, [enabled]);

  return (
    <div
      ref={rootRef}
      data-secondary-view-only={
        enabled ? 'true' : undefined
      }
    >
      {enabled && (
        <style>
          {`
            [data-secondary-view-only="true"]
            [data-secondary-readonly-field="true"] {
              background: #f3f5f7 !important;
              border-color: #cfd6dd !important;
              color: #667784 !important;
              cursor: default !important;
              box-shadow: none !important;
              opacity: 1 !important;
            }

            [data-secondary-view-only="true"]
            input[data-secondary-readonly-field="true"],
            [data-secondary-view-only="true"]
            textarea[data-secondary-readonly-field="true"] {
              caret-color: transparent !important;
            }

            [data-secondary-view-only="true"]
            select[data-secondary-readonly-field="true"],
            [data-secondary-view-only="true"]
            input[type="checkbox"][data-secondary-readonly-field="true"],
            [data-secondary-view-only="true"]
            input[type="radio"][data-secondary-readonly-field="true"],
            [data-secondary-view-only="true"]
            input[type="file"][data-secondary-readonly-field="true"] {
              cursor: not-allowed !important;
            }

            [data-secondary-view-only="true"]
            button[data-secondary-readonly-action="true"] {
              background: #eef1f3 !important;
              border-color: #cfd6dd !important;
              color: #8a98a3 !important;
              cursor: not-allowed !important;
              box-shadow: none !important;
              opacity: 0.72 !important;
            }

            [data-secondary-view-only="true"]
            [contenteditable="false"][data-secondary-readonly-field="true"] {
              min-height: inherit;
            }
          `}
        </style>
      )}

      {children}
    </div>
  );
}


// COURSE_SEMESTER_SORT_V1
function sortCoursesBySemesterAndName(
  courses
) {
  return [
    ...(
      Array.isArray(courses)
        ? courses
        : []
    )
  ].sort(
    (a, b) => {
      const aStart =
        String(
          a?.semesters
            ?.start_date ||
          ''
        );

      const bStart =
        String(
          b?.semesters
            ?.start_date ||
          ''
        );

      /*
       * ISO dates sort chronologically as strings.
       * Newest semester first.
       */
      const byStartDate =
        bStart.localeCompare(
          aStart
        );

      if (byStartDate !== 0) {
        return byStartDate;
      }


      /*
       * Keeps courses from the same semester together even
       * if two semester records happen to share a start date.
       */
      const bySemester =
        String(
          b?.semesters?.name ||
          ''
        ).localeCompare(
          String(
            a?.semesters?.name ||
            ''
          ),
          undefined,
          {
            sensitivity: 'base'
          }
        );

      if (bySemester !== 0) {
        return bySemester;
      }


      const aName =
        String(
          a?.course_titles
            ?.name ||
          a?.name ||
          ''
        );

      const bName =
        String(
          b?.course_titles
            ?.name ||
          b?.name ||
          ''
        );

      return aName.localeCompare(
        bName,
        undefined,
        {
          sensitivity: 'base'
        }
      );
    }
  );
}


async function fetchCourses(session) {
  /*
   * Load every course column.
   *
   * This deliberately avoids maintaining a hand-written list here.
   * New course settings were previously being saved correctly but
   * disappearing after refresh because their columns had not been
   * added to this select list.
   */
  const selectFields =
    '*,course_titles(name),semesters(name,start_date)';

  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  if (session.isSuperadmin) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/courses?select=${selectFields}&order=created_at.desc`,
      { headers }
    );

    if (!response.ok) {
      throw new Error(
        `Loading courses failed (${response.status})`
      );
    }

    const rows =
      await response.json();

    return sortCoursesBySemesterAndName(
      rows
    );
  }

  const [
    ownedResponse,
    accessResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/courses?instructor_id=eq.${session.instructorId}&select=${selectFields}&order=created_at.desc`,
      { headers }
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_access?instructor_id=eq.${session.instructorId}&select=courses(${selectFields})`,
      { headers }
    )
  ]);

  if (!ownedResponse.ok) {
    throw new Error(
      `Loading owned courses failed (${ownedResponse.status})`
    );
  }

  if (!accessResponse.ok) {
    throw new Error(
      `Loading shared courses failed (${accessResponse.status})`
    );
  }

  const owned =
    await ownedResponse.json();

  const accessRows =
    await accessResponse.json();

  const sharedCourses =
    (Array.isArray(accessRows)
      ? accessRows
      : [])
      .map(row => row.courses)
      .filter(Boolean);

  const byId = new Map();

  [
    ...(Array.isArray(owned) ? owned : []),
    ...sharedCourses
  ].forEach(course => {
    byId.set(course.id, course);
  });

  return sortCoursesBySemesterAndName(
    Array.from(
      byId.values()
    )
  );
}

export default function CourseManagement({
  session,
  initialCourseId,
  onCourseSelected
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(initialCourseId || '');
  const [activeTab, setActiveTab] = useState(() => {
    const requested =
      new URLSearchParams(
        window.location.search
      ).get('tab');

    return ALL_TABS.some(
      tab =>
        tab.key === requested
    )
      ? requested
      : 'basic';
  });
  const [loading, setLoading] = useState(true);
  const [
  instructorAccessVersion,
  setInstructorAccessVersion
] = useState(0);
  const [
  hasMultipleInstructors,
  setHasMultipleInstructors
] = useState(false);

  const [
    instructorAccessChecked,
    setInstructorAccessChecked
  ] = useState(false);

  const [
    extendOpen,
    setExtendOpen
  ] = useState(false);
  const [
    importOpen,
    setImportOpen
  ] = useState(false);
  const [
    courseRefreshVersion,
    setCourseRefreshVersion
  ] = useState(0);

  useEffect(() => {
    let cancelled = false;

    fetchCourses({
  instructorId: session.instructorId,
  isSuperadmin: session.isSuperadmin
})
      .then(c => {
        if (cancelled) return;

                const loadedCourses =
          Array.isArray(c) ? c : [];

        setCourses(loadedCourses);

        setSelectedCourseId(current => {
          if (
            current &&
            loadedCourses.some(course => course.id === current)
          ) {
            return current;
          }

          return loadedCourses[0]?.id || '';
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session.instructorId, session.isSuperadmin]);

  useEffect(() => {
    if (
      initialCourseId &&
      courses.some(course => course.id === initialCourseId)
    ) {
      setSelectedCourseId(initialCourseId);
    }
  }, [initialCourseId, courses]);

  useEffect(() => {
    if (selectedCourseId && onCourseSelected) {
      onCourseSelected(selectedCourseId);
    }
  }, [selectedCourseId, onCourseSelected]);

  const selectedCourse =
    courses.find(c => c.id === selectedCourseId) || null;

  const isPrimaryInstructor =
    session.isSuperadmin === true ||
    (
      selectedCourse &&
      selectedCourse.instructor_id ===
        session.instructorId
    );

  const secondaryViewOnly =
    Boolean(
      selectedCourse &&
      !isPrimaryInstructor &&
      !SECONDARY_EDITABLE_TABS.has(
        activeTab
      )
    );

useEffect(() => {
  let cancelled = false;

  if (!selectedCourse?.id) {
    setHasMultipleInstructors(false);
    setInstructorAccessChecked(true);
    return undefined;
  }

  setInstructorAccessChecked(false);

  fetch(
    `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${selectedCourse.id}&select=instructor_id`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  )
    .then(async response => {
      if (!response.ok) {
        throw new Error(
          `Loading course instructors failed (${response.status})`
        );
      }

      return response.json();
    })
    .then(rows => {
      if (cancelled) return;

      const hasAdditionalInstructor =
        (Array.isArray(rows) ? rows : []).some(
          row =>
            row.instructor_id &&
            row.instructor_id !== selectedCourse.instructor_id
        );

      setHasMultipleInstructors(
        hasAdditionalInstructor
      );

      setInstructorAccessChecked(
        true
      );
    })
    .catch(err => {
      console.error(
        'Course instructor check failed:',
        err
      );

      if (!cancelled) {
        setHasMultipleInstructors(false);
        setInstructorAccessChecked(
          true
        );
      }
    });

  return () => {
    cancelled = true;
  };
}, [
  selectedCourse?.id,
  selectedCourse?.instructor_id,
  instructorAccessVersion
]);

  useEffect(() => {
  if (!selectedCourse) return;

  if (
    activeTab === 'attendance' &&
    selectedCourse.attendance_enabled === false
  ) {
    setActiveTab('basic');
    return;
  }

  if (
    activeTab === 'selfeval' &&
    selectedCourse.self_eval_enabled === false
  ) {
    setActiveTab('basic');
    return;
  }

  if (
    activeTab === 'responsibilities' &&
    instructorAccessChecked &&
    !hasMultipleInstructors
  ) {
    setActiveTab('basic');
  }
}, [
  activeTab,
  selectedCourse,
  hasMultipleInstructors,
  instructorAccessChecked
]);

// COURSE_MANAGEMENT_REFRESH_TAB_SYNC
useEffect(() => {
  if (!selectedCourse) {
    return;
  }

  const params =
    new URLSearchParams(
      location.search
    );

  const requested =
    params.get('tab');

  if (!requested) {
    return;
  }

  const requestedTab =
    ALL_TABS.find(
      tab =>
        tab.key === requested
    ) || null;

  const valid =
    !!requestedTab &&
    (
      !requestedTab
        .requiresMultipleInstructors ||
      !instructorAccessChecked ||
      hasMultipleInstructors
    ) &&
    (
      !requestedTab.requiresToggle ||
      selectedCourse[
        requestedTab.requiresToggle
      ] !== false
    );

  if (
    valid &&
    requested !== activeTab
  ) {
    setActiveTab(requested);
    return;
  }

  if (!valid) {
    if (activeTab !== 'basic') {
      setActiveTab('basic');
    }

    params.set(
      'tab',
      'basic'
    );

    navigate(
      `${location.pathname}?${params.toString()}`,
      { replace: true }
    );
  }
}, [
  activeTab,
  selectedCourse,
  hasMultipleInstructors,
  instructorAccessChecked,
  location.pathname,
  location.search,
  navigate
]);

function selectRefreshSafeCourseTab(
  nextTab
) {
  setActiveTab(nextTab);

  const params =
    new URLSearchParams(
      location.search
    );

  params.set(
    'tab',
    nextTab
  );

  navigate(
    `${location.pathname}?${params.toString()}`,
    { replace: true }
  );
}

function handleInstructorAccessChanged() {
  setInstructorAccessVersion(
    current => current + 1
  );
}

  function courseName(course) {
    return (course.course_titles?.name || course.name || 'Untitled') +
      ' — ' + (course.semesters?.name || '');
  }

  function handleCourseUpdated(updated) {
    setCourses(prev =>
      prev.map(c =>
        c.id === updated.id
          ? { ...c, ...updated }
          : c
      )
    );
  }

  async function handleCourseExtended(createdCourse) {
    const loadedCourses = await fetchCourses({
      instructorId: session.instructorId,
      isSuperadmin: session.isSuperadmin
    });

    setCourses(Array.isArray(loadedCourses) ? loadedCourses : []);
    setExtendOpen(false);

    if (createdCourse?.id) {
      setSelectedCourseId(createdCourse.id);
      selectRefreshSafeCourseTab('basic');
    }
  }

  async function handleCourseImported() {
    const loadedCourses = await fetchCourses({
      instructorId: session.instructorId,
      isSuperadmin: session.isSuperadmin
    });

    setCourses(
      Array.isArray(loadedCourses)
        ? loadedCourses
        : []
    );

    setImportOpen(false);
    setCourseRefreshVersion(
      value => value + 1
    );
    selectRefreshSafeCourseTab('basic');
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.heading}>Course Management</h1>
        <div style={styles.headerActions}>
          <select
            style={styles.courseSelect}
            value={selectedCourseId}
            onChange={e => {
              setSelectedCourseId(
                e.target.value
              );
            }}
          >
            <option value="">Select a course</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>
                {courseName(c)}
              </option>
            ))}
          </select>

          {selectedCourse && isPrimaryInstructor && (
            <>
              <button
                type="button"
                style={styles.importButton}
                onClick={() => setImportOpen(true)}
              >
                Import Course
              </button>

              <button
                type="button"
                style={styles.extendButton}
                onClick={() => setExtendOpen(true)}
              >
                Extend Course
              </button>
            </>
          )}
        </div>
      </div>

      {!selectedCourse && (
        <div style={styles.muted}>Select a course to configure it.</div>
      )}

      {selectedCourse && (
        <>
          {importOpen && (
            <CourseImportModal
              course={selectedCourse}
              courses={courses}
              onClose={() => setImportOpen(false)}
              onImported={handleCourseImported}
            />
          )}

          {extendOpen && (
            <CourseExtendModal
              course={selectedCourse}
              session={session}
              onClose={() => setExtendOpen(false)}
              onExtended={handleCourseExtended}
            />
          )}

          <div style={styles.tabs}>
            {ALL_TABS
              .filter(tab => {
  if (
    tab.requiresMultipleInstructors &&
    !hasMultipleInstructors
  ) {
    return false;
  }

  if (
    tab.requiresToggle &&
    selectedCourse[
      tab.requiresToggle
    ] === false
  ) {
    return false;
  }

  return true;
})
              .map(tab => (
                <button
                  key={tab.key}
                  style={{
                    ...styles.tab,
                    ...(activeTab === tab.key ? styles.tabActive : {})
                  }}
                  onClick={() =>
                    selectRefreshSafeCourseTab(
                      tab.key
                    )
                  }
                >
                  {tab.label}
                </button>
              ))}
          </div>

          <div
            key={`${selectedCourse.id}:${courseRefreshVersion}`}
            style={styles.content}
          >
            {activeTab === 'basic' && (
              <ReadOnlyCourseSection
                enabled={
                  secondaryViewOnly
                }
              >
                <CourseBasicSettings
                  course={selectedCourse}
                  session={session}
                  onCourseUpdated={
                    handleCourseUpdated
                  }
                  onInstructorAccessChanged={
                    handleInstructorAccessChanged
                  }
                />
              </ReadOnlyCourseSection>
            )}

            {activeTab === 'syllabus' && (
              <ReadOnlyCourseSection
                enabled={
                  secondaryViewOnly
                }
              >
                <CourseSyllabus
                  course={selectedCourse}
                  session={session}
                  onCourseUpdated={
                    handleCourseUpdated
                  }
                />
              </ReadOnlyCourseSection>
            )}

            {activeTab === 'assignments' && (
              <ReadOnlyCourseSection
                enabled={
                  secondaryViewOnly
                }
              >
                <CourseAssignments
                  course={selectedCourse}
                  session={session}
                />
              </ReadOnlyCourseSection>
            )}

            {activeTab === 'attendance' &&
              selectedCourse.attendance_enabled !== false && (
                <ReadOnlyCourseSection
                  enabled={
                    secondaryViewOnly
                  }
                >
                  <CourseAttendance
                    course={selectedCourse}
                    onCourseUpdated={
                      handleCourseUpdated
                    }
                  />
                </ReadOnlyCourseSection>
              )}

            {activeTab === 'ai' && (
              <ReadOnlyCourseSection
                enabled={
                  secondaryViewOnly
                }
              >
                <CourseAiPermissions
                  course={selectedCourse}
                />
              </ReadOnlyCourseSection>
            )}

            {activeTab === 'meetings' &&
              selectedCourse.self_eval_enabled !== false && (
                <CourseStudentMeetings
                  course={selectedCourse}
                />
              )}

            {activeTab === 'selfeval' &&
              selectedCourse.self_eval_enabled !== false && (
                <ReadOnlyCourseSection
                  enabled={
                    secondaryViewOnly
                  }
                >
                  <CourseSelfEvaluation
                    course={selectedCourse}
                  />
                </ReadOnlyCourseSection>
              )}

            {activeTab === 'students' && (
              <CourseStudents
  course={selectedCourse}
  session={session}
/>
            )}

            {activeTab === 'schedule' && (
              <CourseSchedule
                course={selectedCourse}
              />
            )}

            {activeTab === 'documents' && (
              <CourseDocuments
                course={selectedCourse}
              />
            )}
            {activeTab === 'notifications' && (
  <CourseNotifications
    course={selectedCourse}
    session={session}
  />
)}
{activeTab === 'responsibilities' &&
  hasMultipleInstructors && (
    <ReadOnlyCourseSection
      enabled={
        secondaryViewOnly
      }
    >
      <InstructorResponsibilities
        course={selectedCourse}
      />
    </ReadOnlyCourseSection>
  )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  heading: {
    margin: '0',
    fontSize:
      'clamp(20px, 5vw, 24px)',
    color: '#1f2933'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '16px',
    flexWrap: 'wrap'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    flexWrap: 'wrap',
    flex: '1 1 420px',
    minWidth: 0
  },

  courseSelect: {
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '14px',
    background: 'white',
    minWidth: 0,
    maxWidth: '100%',
    flex: '1 1 280px'
  },

  importButton: {
    border: '1px solid #9fb3c8',
    borderRadius: '8px',
    padding: '9px 13px',
    background: 'white',
    color: '#215b7d',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },

  extendButton: {
    border: 'none',
    borderRadius: '8px',
    padding: '9px 13px',
    background: '#215b7d',
    color: 'white',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: '8px 0'
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    borderBottom: '2px solid #d7dee5',
    marginBottom: '18px',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    WebkitOverflowScrolling:
      'touch'
  },
  tab: {
    border: 'none',
    background: 'transparent',
    padding: '10px 13px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#66788a',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    whiteSpace: 'nowrap',
    flex: '0 0 auto'
  },
  tabActive: {
    color: '#215b7d',
    borderBottom: '2px solid #215b7d'
  },
  viewOnlyNotice: {
    marginBottom: '16px',
    padding: '10px 12px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    background: '#f7f9fa',
    color: '#52616b',
    fontSize: '13px',
    fontWeight: '700'
  },

  content: {
    background: 'white',
    borderRadius: '12px',
    padding:
      'clamp(12px, 3vw, 24px)',
    border: '1px solid #d7dee5',
    boxShadow:
      '0 6px 20px rgba(0,0,0,0.08)',
    minWidth: 0,
    overflowX: 'hidden'
  }
};